import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb2"
import CampaignPermissionNotice from "components/Common/CampaignPermissionNotice"
import TableContainer from "components/Common/TableContainer2"
import useCurrentPermissions from "hooks/useCurrentPermissions"
import { del } from "helpers/api_helper"
import { getAllPages } from "helpers/api_pagination_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import { channelLabel, couponApi, displayDate } from "./couponHelpers"

export default function CouponList() {
  const permissions = useCurrentPermissions("Unable to verify coupon permissions.")
  const canCreate = permissions.hasScope("coupon:create")
  const canUpdate = permissions.hasScope("coupon:update")
  const canDelete = permissions.hasScope("coupon:delete")
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [selected, setSelected] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState("")
  const deleteLock = useRef(false)
  const reload = useCallback(async () => {
    setLoading(true)
    setError("")
    try { setRows(await getAllPages(couponApi())) }
    catch (failure) { setError(getApiErrorMessage(failure, "Unable to load coupons.")) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { reload() }, [reload])
  useEffect(() => { document.title = "Coupons & Vouchers | " + process.env.REACT_APP_CMS_TITLE }, [])

  const removeCoupon = async () => {
    if (!selected || !canDelete || deleteLock.current) return
    deleteLock.current = true
    setDeleting(true)
    setDeleteError("")
    try {
      await del(`${couponApi()}/${selected.id}`)
      setRows(current => current.filter(row => row.id !== selected.id))
      setSelected(null)
    } catch (failure) {
      setDeleteError(getApiErrorMessage(failure, "Unable to delete coupon."))
    } finally {
      deleteLock.current = false
      setDeleting(false)
    }
  }

  const columns = useMemo(() => [
    { header: "Name", accessorKey: "name" },
    { header: "Type", accessorKey: "type", cell: ({ getValue }) => getValue() === "voucher" ? "Voucher" : "Coupon" },
    { header: "Channel", accessorKey: "entry_channel", cell: ({ getValue }) => channelLabel(getValue()) },
    { header: "Campaign", accessorFn: row => row.campaign?.campaign_name || `#${row.line_campaign_id}` },
    { header: "Available / Total", accessorFn: row => `${row.counts?.available ?? 0} / ${row.counts?.total ?? 0}` },
    { header: "Sent", accessorFn: row => row.counts?.sent ?? 0 },
    { header: "Expires", accessorKey: "expires_at", cell: ({ getValue }) => displayDate(getValue()) },
    { header: "Status", accessorKey: "active", cell: ({ getValue }) => <span className={`badge bg-${getValue() ? "success" : "secondary"}`}>{getValue() ? "Active" : "Inactive"}</span> },
    { header: "Tools", id: "tools", enableSorting: false, cell: ({ row }) => (
      <div className="d-flex gap-2">
        <Link className="btn btn-sm btn-info" to={`/coupon/view/${row.original.id}`}>View</Link>
        {canUpdate && <Link className="btn btn-sm btn-warning" to={`/coupon/edit/${row.original.id}`}>Edit</Link>}
        {canDelete && <button type="button" className="btn btn-sm btn-danger" aria-label={`Delete ${row.original.name}`} onClick={() => { setSelected(row.original); setDeleteError("") }}>Delete</button>}
      </div>
    ) },
  ].map(column => ({ enableColumnFilter: false, ...column })), [canUpdate, canDelete])

  return (
    <div className="page-content"><div className="container-fluid">
      <Breadcrumbs title="Coupons & Vouchers" breadcrumbItems={[{ title: "Coupons & Vouchers", link: "coupon" }]} />
      <div className="bInnerContent">
        <p className="text-muted">Manage reward codes and automatic LINE delivery after a survey passes.</p>
        <CampaignPermissionNotice permissions={permissions} permissionLabel="coupon" />
        <div className="d-flex justify-content-between align-items-start mb-3 gap-3">
          <button type="button" className="btn btn-outline-secondary" disabled={loading} onClick={reload}>Refresh</button>
          <div className="text-end">
            {canCreate ? <Link className="btn btn-primary" to="/coupon/create"><i className="bx bx-plus me-1" />Create coupon / voucher</Link>
              : <button type="button" className="btn btn-primary" disabled>Create coupon / voucher</button>}
            {!permissions.loading && !permissions.error && !canCreate && <small className="d-block text-muted mt-1">Your account does not have permission to create coupons.</small>}
          </div>
        </div>
        {error && <Alert color="danger">{error}</Alert>}
        {loading ? <Spinner color="primary" aria-label="Loading coupons" /> : !error && (
          rows.length ? <TableContainer columns={columns} data={rows} isGlobalFilter isPagination isCustomPageSize
            isAddButton={false} isExportButton={false} isExportToExcel={false} SearchPlaceholder="Search coupons"
            pagination="pagination" paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap w-100" rootMenu="coupon" />
            : <div className="text-center border rounded p-5"><i className="bx bx-purchase-tag fs-1 text-muted" /><h5 className="mt-3">No coupons or vouchers yet</h5><p className="text-muted mb-0">Create a reward, choose its entry channel and LINE campaign, then import codes.</p></div>
        )}
      </div>
      <Modal isOpen={Boolean(selected)} toggle={() => !deleting && setSelected(null)} centered>
        <ModalHeader toggle={() => !deleting && setSelected(null)}>Delete coupon / voucher</ModalHeader>
        <ModalBody>
          <p>Delete <strong>{selected?.name}</strong>? New allocations and allocations awaiting stock will stop. Already allocated rewards will still be delivered.</p>
          {deleteError && <Alert color="danger">{deleteError}</Alert>}
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" disabled={deleting} onClick={() => setSelected(null)}>Cancel</button>
            <button type="button" className="btn btn-danger" disabled={deleting || !canDelete} onClick={removeCoupon}>{deleting ? "Deleting..." : "Confirm delete"}</button>
          </div>
        </ModalBody>
      </Modal>
    </div></div>
  )
}
